import { cloneElement, useCallback, useEffect, type ReactElement } from "react";
import { useController, useFormContext, useWatch, type RegisterOptions } from "react-hook-form";
import { Label } from "../../../ui/label";
import { cn } from "../../../../lib/utils";

type FieldLayout = "row" | "column";

interface FieldProps {
    label?: string;
    name: string;
    className?: string;
    required?: boolean;
    valuePropName?: "checked" | "value";
    deps?: string[];
    // Defaults to true so fields that mount/unmount conditionally (e.g. based on
    // another field's value) don't leave stale values behind. Pass false for fields
    // that unmount for reasons unrelated to the field's own relevance - e.g. inside a
    // modal that's closed and reopened - so the value survives the unmount.
    shouldUnregister?: boolean;
    rules?: RegisterOptions;
    layout?: FieldLayout;
    children: ReactElement<any>;
}

function Field({
    label,
    name,
    className,
    children,
    required = false,
    valuePropName = "value",
    deps,
    shouldUnregister = true,
    rules,
    layout = "column",
}: FieldProps) {
    const { control, trigger } = useFormContext();
    const { field, fieldState } = useController({
        name,
        control,
        shouldUnregister,
        rules,
    });

    // 1. Direct error extraction from useController
    const error = fieldState.error;

    const depsValues = useWatch({ control, name: deps ?? [] });

    // 2. Combined and robust cross-validation effect
    useEffect(() => {
        // `isDirty`/`isTouched` come from react-hook-form itself, so a field that's
        // still pristine (never interacted with) won't validate here - avoiding a
        // "required" error before the user has touched it or submitted the form.
        // Once touched, it keeps re-validating on every change (including clearing
        // it back out) or while it's already invalid, so it stays in sync as deps
        // change too.
        if (fieldState.isDirty || fieldState.isTouched || error) {
            trigger(name);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [depsValues, field.value]);

    // 3. Normalize props for standard inputs vs Radix UI primitives. The child's own
    // onChange/onCheckedChange (if any) is composed with, not replaced by, the field's -
    // so a caller can still hook side effects (e.g. resetting a sibling field) onto the
    // same change event that also updates this field's value.
    const getControlProps = useCallback(() => {
        switch (valuePropName) {
            case "checked": {
                const childOnCheckedChange = children.props.onCheckedChange;
                return {
                    name: field.name,
                    ref: field.ref,
                    onBlur: field.onBlur,
                    checked: field.value ?? false,
                    onCheckedChange: (checked: boolean) => {
                        childOnCheckedChange?.(checked);
                        field.onChange(checked);
                    },
                };
            }
            default: {
                const childOnChange = children.props.onChange;
                return {
                    ...field,
                    onChange: (...args: unknown[]) => {
                        childOnChange?.(...args);
                        field.onChange(...args);
                    },
                };
            }
        }
    }, [field, valuePropName, children.props.onCheckedChange, children.props.onChange]);

    const renderedControl = cloneElement(children, {
        ...getControlProps(),
        className: cn(
            children.props.className,
            error?.message &&
            "tw-border-red-500 focus-visible:tw-border-red-500 focus-visible:tw-ring-red-500/20",
        ),
    });

    const errorMessage = error?.message && (
        <div className="tw-text-red-500 tw-text-sm tw-text-start tw-break-words">
            {error.message}
        </div>
    );

    if (layout === "row") {
        return (
            <div className={cn("tw-grid tw-grid-cols-[150px_1fr] tw-gap-x-3 tw-gap-y-1 tw-mb-6", className)}>
                {label && (
                    <Label htmlFor={name} className="tw-flex tw-shrink-0 tw-items-center tw-gap-1 tw-font-semibold tw-mb-0">
                        {label}
                        {required && <span className="tw-text-red-500">*</span>}
                    </Label>
                )}

                <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col">
                    {renderedControl}
                </div>

                {errorMessage && (
                    <div className="tw-col-start-2">
                        {errorMessage}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className={cn("tw-flex tw-min-w-0 tw-flex-col tw-gap-2 tw-mb-6", className)}>
            {label && (
                <Label htmlFor={name} className="tw-flex tw-items-center tw-gap-1 font-semibold">
                    {label}
                    {required && <span className="tw-text-red-500">*</span>}
                </Label>
            )}

            {renderedControl}
            {errorMessage}
        </div>
    );
}

export default Field;
